// tslint:disable:no-submodule-imports
import { ApolloClient } from 'apollo-client';
import React, { FC, useContext } from 'react';
import { withApollo, WithApolloClient } from 'react-apollo';
import { useHistory } from 'react-router-dom';
import { ActionMeta, ValueType } from 'react-select';
import AsyncSelect from 'react-select/async';
import styled from 'styled-components';
import { IAddress, ICustomer, ISoldTo } from '../../graphql/types';
import { soldToRoute } from '../../pages/sold-to/route';
import { RegionsContext } from '../../regions-context';
import { Black, Black2, White } from '../../styles/colours';
import { Option } from './option';
import {
  GET_CUSTOMER,
  SEARCH_CUSTOMERS,
  SEARCH_SOLD_TOS_BY_NAME,
} from './queries';

import { flatten } from 'lodash';
import { getUserSalesOrgs } from 'src/libs/auth';
import { GetListOfAllCob } from '../../pages/list-of-lob/queries/all-cob-list';
import { ALL_REGIONS } from '../regions-menu/regions-menu';

export interface ISelected {
  value: string;
  label: string;
}

interface IApolloSelectProps {
  client?: ApolloClient<any>;
  onSelect: (selected: ValueType<ISelected>, action: ActionMeta) => void;
}

type DropDownProps = WithApolloClient<IApolloSelectProps>;

interface ICallback {
  value: string;
  soldToId: string;
  shipToId?: string;
  address: IAddress;
  label: string;
  cob: string;
  type: 'Sold-To' | 'Ship-To';
}

const StyledSelect = styled.div`
  width: 42rem;
  z-index: 3;

  @media print {
    display: none;
  }

  .react-select__control {
    background-color: ${White};
    color: ${Black};
    line-height: 1;
    border-radius: 4px;
    border: 1px solid transparent;
    padding: 0.5rem 1rem;
    box-shadow: none;
  }

  .react-select__value-container {
    padding: 0;
  }

  .react-select__placeholder {
    max-height: 1rem;
  }

  .react-select__input {
    max-height: 1rem;
  }

  .react-select__loading-indicator {
    padding: 4px;
  }

  .react-select__menu {
    border-radius: 2px;
    box-shadow: 0 2px 8px 0 ${Black2};
    margin-top: 1px;
    margin-bottom: 0;
    z-index: 999;
  }
`;

const DropDown: FC<DropDownProps> = ({ client, onSelect }) => {
  const orgs = getUserSalesOrgs();
  const { regions } = useContext(RegionsContext);
  let salesOrgs = flatten(
    regions
      .filter(region => ALL_REGIONS[region])
      .map(region => ALL_REGIONS[region].salesOrgs)
  );
  if (salesOrgs.length === 0) {
    salesOrgs = orgs;
  }

  const allCob = GetListOfAllCob();
  let cob: string | any[] | null = [];
  const activeCob = 'activeCob';
  if (localStorage.getItem(activeCob)) {
    cob = JSON.parse(localStorage.activeCob);
  } else {
    if (allCob !== undefined) {
      const requiredKeys = ['dcVtweg', 'divisionSpart'];
      cob = allCob.map(coblist =>
        Object.assign({}, ...requiredKeys.map(key => ({ [key]: coblist[key] })))
      );
      localStorage.setItem('allowedCob', JSON.stringify(cob));
    }
  }

  const loadOptions = async (inputValue: any, callback: any) => {
    if (inputValue.length > 0) {
      const responses = await Promise.all([
        client.query({
          query: SEARCH_SOLD_TOS_BY_NAME,
          variables: { name: inputValue, salesOrgs, businessAreas: cob },
        }),
        client.query({
          query: GET_CUSTOMER,
          variables: { id: inputValue, salesOrgs, businessAreas: cob },
        }),
        client.query({
          query: SEARCH_CUSTOMERS,
          variables: { name: inputValue, salesOrgs, businessAreas: cob },
        }),
      ]);
      const [
        getSoldToResponse,
        getCustomerToResponse,
        searchCustomers,
      ] = responses;

      let options: ICallback[] = [];

      if (getSoldToResponse.data) {
        options = getSoldToResponse.data.soldToSearch.map(
          (soldTo: ISoldTo) => ({
            value: soldTo.id,
            soldToId: soldTo.id,
            label: soldTo.address.name,
            address: soldTo.address,
            cob: soldTo.distributionChannel,
            type: 'Sold-To',
          })
        );
      }

      if (getCustomerToResponse.data.customer) {
        const shipTo = getCustomerToResponse.data.customer.shipTo;
        const soldTo = getCustomerToResponse.data.customer.soldTo;
        if (shipTo) {
          options = [
            {
              value: soldTo.id,
              soldToId: soldTo.id,
              shipToId: shipTo.id,
              label: shipTo.address.name,
              address: shipTo.address,
              cob: soldTo.distributionChannel,
              type: 'Ship-To',
            },
          ];
        } else {
          options = [
            {
              value: soldTo.id,
              soldToId: soldTo.id,
              label: soldTo.address.name,
              address: soldTo.address,
              cob: soldTo.distributionChannel,
              type: 'Sold-To',
            },
          ];
        }
      } else if (searchCustomers.data.searchCustomers) {
        searchCustomers.data.searchCustomers.forEach((customer: ICustomer) => {
          const { soldTo, shipTo } = customer;
          if (shipTo && shipTo.address) {
            options.push({
              value: soldTo.id,
              soldToId: soldTo.id,
              shipToId: shipTo.id,
              label: shipTo.address.name,
              address: shipTo.address,
              cob: soldTo.distributionChannel,
              type: 'Ship-To',
            });
          }
        });
      }
      if (options.length > 0) {
        callback(options);
      }
    }
  };

  return (
    <StyledSelect data-type="search-dropdown">
      <AsyncSelect
        autoFocus={true}
        classNamePrefix="react-select"
        cacheOptions={false}
        loadOptions={loadOptions}
        defaultOptions={true}
        onChange={onSelect}
        components={{ Option }}
        maxMenuHeight={600}
        placeholder="Search by customer name or id"
        styles={{
          menuPortal: provided => ({
            ...provided,
            zIndex: 9999,
          }),
        }}
        menuPortalTarget={document.querySelector('body')}
      />
    </StyledSelect>
  );
};

const SearchDropDown = withApollo<IApolloSelectProps>(DropDown);

const CustomerSearchDropdown: FC = () => {
  const history = useHistory();
  const onSelect = (props: any) => {
    const { soldToId, shipToId } = props;
    history.push(
      soldToRoute(soldToId, {
        shipToId,
      })
    );
  };
  return <SearchDropDown onSelect={onSelect} />;
};

export { CustomerSearchDropdown };
